import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getMessaging, getToken, deleteToken } from 'firebase/messaging';
import jwtDecode from 'jwt-decode';

// firebase configuration
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Check if the browser is supported or not for push notifications, service worker, and push manager which are required for push notifications
export const isSupported = () =>
    'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

// Initialize Firebase app
const firebaseApp = initializeApp(firebaseConfig);

// Export firestore database
export const db = getFirestore(firebaseApp);

// Export auth
export const auth = getAuth(firebaseApp);

// Export messaging and check if the browser is supported or not
// If the browser is supported, then get the messaging instance from Firebase
export const messaging = isSupported() ? getMessaging(firebaseApp) : null;

// Send request to get the token for the client browser
export async function sendRequest() {
    if (isSupported()) {
        // console.log('Requesting User Permission……');

        if (Notification.permission === 'granted') {
            // console.log('Notification User Permission Granted.');

            try {
                const currentToken = await getToken(messaging, {
                    vapidKey: process.env.REACT_APP_FIREBASE_VAPI_KEY
                });
                if (currentToken) {
                    // console.log(`Device Token: `, currentToken);
                    localStorage.setItem('deviceToken', currentToken);
                } else {
                    console.log(`Failed to generate the registration token.`);
                    localStorage.removeItem('deviceToken');
                }
            } catch (err) {
                console.log(`An error occurred when requesting to receive the token.`, err);
                localStorage.removeItem('deviceToken');
            }
        } else {
            const permission = await Notification.requestPermission();

            if (permission === 'granted') {
                console.log('Notification User Permission Granted.');

                try {
                    const currentToken = await getToken(messaging, {
                        vapidKey: process.env.REACT_APP_FIREBASE_VAPI_KEY
                    });
                    if (currentToken) {
                        // console.log(`Device Token: `, currentToken);
                        localStorage.setItem('deviceToken', currentToken);
                    } else {
                        console.log(`Failed to generate the registration token.`);
                    }
                } catch (err) {
                    console.log(`An error occurred when requesting to receive the token.`, err);
                    localStorage.removeItem('deviceToken');
                }
            } else if (permission === 'denied') {
                localStorage.removeItem('deviceToken');
            }
        }
    } else {
        console.log('Browser Not Supported');
    }
}
// delete device token for the client browser
export async function deleteTokenRequest() {
    await deleteToken(messaging);
}

// decode firebase token
export async function decodeFirebaseToken(firebaseToken) {
    // console.log("Before Firebase Token Decoded => UID ", firebaseToken);
    const decoded = jwtDecode(firebaseToken);
    // console.log("After Firebase Token Decoded => UID: ", decoded?.uid);
    return decoded?.uid;
}

// The getMessaging function in Firebase is used to initialize and retrieve an instance of Firebase Cloud Messaging (FCM).
// FCM is a service that allows you to send notifications and messages to users across different platforms (iOS, Android, and web).

// The getFirestore function in Firebase is used to initialize and retrieve an instance of Cloud Firestore, which is Firebase's NoSQL cloud database.
// Cloud Firestore allows you to store, sync, and query data for your web and mobile applications.

// The getAuth function in Firebase is used to initialize and retrieve an instance of Firebase Authentication.
// Firebase Authentication provides backend services, easy-to-use SDKs, and ready-made UI libraries to authenticate users to your app.
// It supports authentication using passwords, phone numbers, popular federated identity providers like Google, Facebook, and Twitter, and more.

// The getToken function is used to retrieve the current registration token for the client app instance.
// This token is unique to the app instance and is required to send messages to that specific device.

// The deleteToken function is used to delete the current registration token for the client app instance.
// This is useful when you want to stop receiving messages on a particular device or when the user logs out.
